import type { HTMLAttributes, Key, MouseEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import PowerIconButton from '@erikdakoda/ui/components/PowerIconButton';
import {
  autocompleteTextFieldSlotProps,
  omitAutocompleteTextFieldParams,
} from '@erikdakoda/ui/shared/autocompleteTextFieldSlotProps';
import type { CustomControlProps, SelectOption } from '@erikdakoda/editor-ui/types';
import { Controller, useFormContext, type FieldValues } from 'react-hook-form';

import ToolSetEditor from './ToolSetEditor';

type ToolSetControlProps = CustomControlProps<FieldValues>;

function isRefetchToolSets(value: unknown): value is () => Promise<void> {
  return typeof value === 'function';
}

function getRefetchToolSets(
  props: Record<string, unknown> | undefined,
): (() => Promise<void>) | null {
  const refetch = props?.refetchToolSets;
  return isRefetchToolSets(refetch) ? refetch : null;
}

/**
 * Bot editor tool set picker with an edit button that opens {@link ToolSetEditor} in a dialog.
 */
export default function ToolSetControl(props: ToolSetControlProps) {
  'use memo';

  const { t } = useTranslation('tool-set-ui');
  const {
    fieldName,
    control,
    fieldSpec,
    item,
    disabled,
    className,
    required,
    label,
    formControlSx,
    errors,
  } = props;
  const { setValue } = useFormContext<FieldValues>();
  const options = fieldSpec?.options ?? [];
  const refetchToolSets = getRefetchToolSets(fieldSpec?.props);
  const [editingToolSetId, setEditingToolSetId] = useState<string | null>(null);
  const isError = Boolean(errors[fieldName]);
  const helperOrErrorText = isError ? String(errors[fieldName]?.message) : fieldSpec?.helperText;
  const renderOption =
    fieldSpec?.renderOption ??
    ((optionProps: HTMLAttributes<HTMLLIElement> & { key: Key }, option: SelectOption) => {
      const { key, ...liProps } = optionProps;
      return (
        <li key={key} {...liProps}>
          {(option.label || option.name) ?? option.id.toString()}
        </li>
      );
    });

  function handleEditClick(event: MouseEvent<HTMLButtonElement>, toolSetId: string): void {
    event.preventDefault();
    event.stopPropagation();
    setEditingToolSetId(toolSetId);
  }

  function handleCloseEditor(): void {
    setEditingToolSetId(null);
  }

  return (
    <>
      <Controller
        // @ts-expect-error react-hook-form Controller does not type className; root className convention
        className="tool-set-control"
        name={fieldName}
        defaultValue={item?.[fieldName]}
        control={control}
        rules={{ required }}
        render={({ field: { value, onBlur } }) => {
          const toolSetId = value == null ? '' : String(value);
          const selectedOption: SelectOption | null =
            options.find((option) => option.id === toolSetId) ?? null;

          return (
            <FormControl sx={formControlSx} className={className}>
              <Autocomplete
                value={selectedOption}
                disabled={disabled}
                autoSelect
                autoHighlight
                blurOnSelect
                disableClearable={required}
                options={options}
                isOptionEqualToValue={(option, selected) => option?.id === selected?.id}
                renderOption={renderOption}
                getOptionDisabled={(option) => option.disabled ?? false}
                getOptionLabel={(option) => (option.label || option.name) ?? option.id?.toString()}
                onChange={(_event, newValue) => {
                  const nextValue =
                    newValue == null
                      ? null
                      : typeof newValue === 'string' || typeof newValue === 'number'
                        ? newValue
                        : (newValue.id ?? null);
                  setValue(fieldName, nextValue, { shouldDirty: true });
                  onBlur();
                }}
                renderInput={(params) => (
                  <TextField
                    {...omitAutocompleteTextFieldParams(params)}
                    label={label}
                    required={required}
                    error={isError}
                    helperText={helperOrErrorText}
                    sx={{
                      '& .tool-set-control-edit-button': {
                        visibility: 'hidden',
                      },
                      '&:hover .tool-set-control-edit-button': {
                        visibility: toolSetId === '' ? 'hidden' : 'visible',
                      },
                    }}
                    slotProps={autocompleteTextFieldSlotProps(params, {
                      input: {
                        endAdornment: (
                          <>
                            {params.slotProps.input.endAdornment}
                            <InputAdornment className="tool-set-control-edit-button" position="end">
                              <PowerIconButton
                                icon="pen-to-square"
                                title={t('editToolSet')}
                                aria-label={t('editToolSet')}
                                onClick={(event) => handleEditClick(event, toolSetId)}
                                disabled={disabled || !toolSetId}
                                size="small"
                                padding={3}
                              />
                            </InputAdornment>
                          </>
                        ),
                      },
                    })}
                  />
                )}
                {...fieldSpec?.innerProps}
              />
            </FormControl>
          );
        }}
      />
      {editingToolSetId ? (
        <ToolSetEditor
          toolSetId={editingToolSetId}
          dialog
          refetch={refetchToolSets}
          onClose={handleCloseEditor}
        />
      ) : null}
    </>
  );
}
